
import React, { useState, useEffect, lazy, useRef } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";

// Mui
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';

// Redux Store
import { useDispatch, useSelector } from "react-redux";
import { setLogged } from './redux/slices/logged.js';

// componentes
import { NavBar } from './components/NavBar';

// request
import { logoutKeycloak } from './request/keycloak/logoutKeycloak.js';
// import { authKeycloak } from './request/keycloak/authKeycloak.js';

// helpers
import temaClaro from './helpers/temaClaro';
import temaOscuro from './helpers/temaOscuro';
import { decryptData } from './helpers/decryptData.js';
import { isTokenExpired } from './helpers/isTokenExpired.js';
import { generatePaths } from './helpers/generatePaths.js';
// import { getUserRols } from './helpers/getUserRols.js';

// css
import './App.css'
import { decodeJwt } from 'jose';

// pages
const Home = lazy(() => import("./pages/Home"));


// Componente principal de la aplicación
function App() {

	const location = useLocation();
	const dispatch = useDispatch();
	const logged = useSelector(state => state.loggedStore.value)

	const navigate = useNavigate();

	const [theme, setTheme] = useState(
		localStorage.getItem('theme')
			? localStorage.getItem('theme')
			: 'light'
	)

	const pathLabels = generatePaths(logged, theme)

	useEffect(() => {
		// No guardamos la ruta comodín (*) ni /home si quieres empezar siempre desde ahí
		if (location.pathname !== '/' && location.pathname !== '/home') {
			sessionStorage.setItem('lastPath', location.pathname);
		}
	}, [location]);

	useEffect(() => {
		let expirationTimer;

		const logout = async () => {
			await logoutKeycloak(logged.token);
			sessionStorage.clear();
			dispatch(setLogged({ state: false, token: null }));
			window.location.href = '/home';
		};

		const checkExpiration = () => {
			if (!logged.state || !logged.token) return;

			if (isTokenExpired(logged.token)) {
				logout();
			} else {
				// Programar logout justo cuando expire
				const decoded = decodeJwt(logged.token);
				const expMs = decoded.exp * 1000;
				const timeUntilExp = expMs - Date.now();
				console.log("Tiempo de sesion: ", timeUntilExp);

				expirationTimer = setTimeout(logout, timeUntilExp);
			}
		};

		checkExpiration();

		return () => clearTimeout(expirationTimer);
	}, [logged.token, logged.state]);


	useEffect(() => {
		(async function () {

			if (sessionStorage.dt) {
				const tokenEncrypt = sessionStorage.getItem('dt')
				const dataDescrypt = await decryptData(tokenEncrypt)

				if (dataDescrypt) {
					const { token, user, pass, roles } = dataDescrypt

					dispatch(
						setLogged({
							roles,
							state: true,
							token: token,
							credentials: {
								user: user,
								pass: pass
							}
						})
					)
				} else {
					sessionStorage.removeItem('dt')
					navigate('/home');
					// logoutKeycloak(logged.token);
				}
			}
		})()
	}, [])

	const styleBox = {
		height: '96vh',
		padding: '2vh',
		display: 'flex',
		overflowY: 'scroll',
		alignItems: 'center',
		fontFamily: 'Roboto',
		color: 'text.primary',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		bgcolor: 'background.default',
	}

	return (
		<ThemeProvider theme={theme === 'light' ? temaClaro : temaOscuro}>
			<Box id={'Box_app'} sx={styleBox}>

				<NavBar setTheme={setTheme} theme={theme} menus={pathLabels.labels} />

				<Routes>
					<Route index
						element={
							<React.Suspense fallback={<>...</>}>
								<Home />
							</React.Suspense>
						}
					/>

					{
						pathLabels.paths.map((obj, idxObj) =>
							<Route
								key={idxObj}
								path={obj.path}
								element={
									<React.Suspense fallback={<>...</>}>
										{obj.render}
									</React.Suspense>
								}
							/>
						)
					}

					{/* Ruta comodín para redirigir a /home */}
					<Route
						path="*"
						element={<NavigateToLastPath />}
					/>
				</Routes>
			</Box>
		</ThemeProvider>
	);
}

function NavigateToLastPath() {
	const lastPath = sessionStorage.getItem('lastPath');
	return <Navigate to={lastPath || '/home'} replace />;
}

export default App;
